import FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { PrintButton } from 'components/PrintButton';
import colors from 'components/ThemeProvider/colors';
import useTheme from 'hooks/useTheme';
import { timeout } from 'shared/timeout';
import { useGetTrackEventsReport } from './gql/useGetTrackEventsReport.mutation';

export const PrintTrackEventsReport = ({ text, headerText }) => {
  const getTrackEventsReport = useGetTrackEventsReport();
  const [disabled, setDisabled] = useState(false);
  const theme = useTheme();

  let filename = '';

  const getData = async () => {
    try {
      const response = await getTrackEventsReport();
      let title = '';
      const eventsReport = response.data.getTrackEventsReport;

      title = moment().format('MMM DD - YYYY h:mm A').toUpperCase();

      filename = headerText + 'Events List - ' + title;
      return { eventsReport, title, filename };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);

      toast.error('Error generating report');
      setDisabled(false);
    }
  };

  function generatePdf() {
    setDisabled(true);
    return new Promise(async (resolve) => {
      const pdf = await exportPDF();

      setDisabled(false);
      if (filename) {
        pdf.setProperties({
          title: filename,
        });
      }
      if (
        navigator.userAgent.includes('iPhone') &&
        navigator.maxTouchPoints > 1
      ) {
        var blob = new Blob([pdf.output('blob')], {
          type: 'data:application/pdf,',
        });
        FileSaver.saveAs(blob, filename + '.pdf');
      } else if (navigator.maxTouchPoints > 1) {
        pdf.output('datauri', filename + '.pdf');
      } else {
        window.open(
          pdf.output('bloburl', { filename: filename + '.pdf' }),
          '_blank'
        );
      }
      timeout(resolve, 6000);
    });
  }

  //This will generate the spectator PDF report
  const exportPDF = async () => {
    const { eventsReport, title, filename } = await getData();
    const unit = 'pt';
    const size = 'A4'; // Use A1, A2, A3 or A4
    const orientation = 'landscape'; // portrait or landscape

    const doc = new jsPDF(orientation, unit, size, true);

    doc.setFontSize(15);
    doc.setFontStyle('bold');

    const pageSize = doc.internal.pageSize;
    const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
    const pageHeight = pageSize.height ?? pageSize.getHeight();

    const text = doc.splitTextToSize(title, pageWidth - 650, {});
    doc.text(text, 40, 40);
    doc.text(headerText, 40, 60);
    doc.text('Events List', 620, 100);

    const headers = [['DATE', 'EVENT', 'ON SALE DATE', 'STATUS']];

    const tableData = eventsReport.results.map((event) => {
      const {
        isMultiDay,
        day,
        date,
        month,
        listDates,
        year,
        name,
        sale_start,
        status = '',
      } = event;

      return [
        isMultiDay
          ? `${month.toUpperCase()} ${listDates} - ${year}`
          : `${day} ${date} - ${year}`,
        name,
        moment(sale_start, 'MM-DD-YYYY')
          .format('ddd MMM D - YYYY')
          .toUpperCase() ?? '',
        status,
      ];
    });

    doc.autoTable({
      startY: 120,
      rowPageBreak: 'avoid',
      columns: [
        { dataKey: 'Date', header: 'DATE' },
        { dataKey: 'Event', header: 'EVENT' },
        { dataKey: 'On Sale Date', header: 'ON SALE DATE' },
        { dataKey: 'Status', header: 'STATUS' },
      ],
      head: headers,
      body: tableData,
      styles: {
        fontStyle: 'bold',
      },
      headStyles: {
        fillColor: '#fa4616',
      },
      columnStyles: {
        Event: {
          cellWidth: 200,
          cellPadding: { top: 5, right: 5, bottom: 5, left: 5 },
          textColor: theme.colors.text.black,
        },
      },
      didParseCell: function (tableData) {
        const hasNoWaiver = tableData.row.raw[6] === false;

        if (hasNoWaiver) {
          tableData.cell.styles.fillColor = '#feece8';
          tableData.cell.styles.textColor = colors.text.gray;
          tableData.cell.styles.opacity = 0.1;
        }
      },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 0; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setFontStyle('normal');
      const dateTimeStamp = moment()
        .format('MMM DD - YYYY h:mm A')
        .toUpperCase();
      doc.text(`Exported ${dateTimeStamp}`, pageWidth - 40, pageHeight - 20, {
        align: 'right',
      });
    }
    // Set typography back to default
    doc.setFontSize(15);
    doc.setFontStyle('bold');

    doc.setProperties({
      title: filename,
      subject: 'Events List',
      author: 'Pit Pay',
    });
    return doc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  return (
    <div>
      <PrintButton
        buildPdf={generatePdf}
        text={text}
        variant="minimal"
        disabled={disabled}
      />
    </div>
  );
};
